import { sendMessageNotificationToDriverCase } from '@/features/driver/domain/usecases/sendMessageNotificationToDriverCase'
import { ChatRepository } from '../../domain/repositories/ChatRepository'
import { SupportMessagesService } from '@/backend/market'
import type { ChatNotificationHistoryFilterEntity } from '../../domain/entities/ChatNotificationHistoryFilterEntity'
import { ChatNotificationHistoryFilterModel } from '../models/ChatNotificationHistoryFilterModel'
import { PaginateEntity } from '@/shared/entities/PaginateEntity'
import { ChatModel } from '../models/ChatModel'
import type { ChatEntity } from '../../domain/entities/ChatEntity'
import useSignalRConnection from '@/utils/signalRConnection'
import { useNotificationStore } from '../../presentation/stores/notificationStore'
import { UnavailableItemOrderEntity } from '@/features/order/domain/entities/UnavailableItemOrderEntity'

export class ChatRepositoryImpl implements ChatRepository {
  async getNotificationHistory(
    filterData: ChatNotificationHistoryFilterEntity
  ): Promise<PaginateEntity<ChatEntity[]> | { error: true }> {
    const filterModel = new ChatNotificationHistoryFilterModel(filterData)
    try {
      const response = await SupportMessagesService.getAllSupportMessagesAdmin(filterModel)
      const result = response.items?.map((item) => new ChatModel(item as ChatEntity))
      const chatNotificationResponse = new PaginateEntity<ChatModel[]>(response, result)
      return chatNotificationResponse
    } catch {
      return { error: true }
    }
  }
  async getMessagesHistory(
    filterData: ChatNotificationHistoryFilterEntity
  ): Promise<PaginateEntity<ChatEntity[]> | { error: true }> {
    const filterModel = new ChatNotificationHistoryFilterModel(filterData)
    try {
      const response =
        await SupportMessagesService.getSupportMessagesByServantUserIdAdmin(filterModel)
      const result = response.items?.map((item) => new ChatModel(item as ChatEntity))
      const chatNotificationResponse = new PaginateEntity<ChatModel[]>(response, result)
      return chatNotificationResponse
    } catch {
      return { error: true }
    }
  }
  async sendMessage(userId: string, message: string): Promise<{ success: true } | { error: true }> {
    try {
      await SupportMessagesService.postDriverMessageAdmin({
        requestBody: {
          message,
          driverUserId: userId
        }
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async updateReadMessage(userId: string): Promise<{ success: true } | { error: true }> {
    try {
      await SupportMessagesService.setSupportMessageAsReadAdmin({
        senderUserId: userId
      })
      return { success: true }
    } catch {
      return { error: true }
    }
  }
  async connectToNotificationChannel(): Promise<ChatModel | { error: true }> {
    try {
      const signalRConnection = useSignalRConnection()
      const notificationStore = useNotificationStore()
      return new Promise((resolve) => {
        signalRConnection.on('SupportMessageReceived', (response: string) => {
          if (JSON.parse(response)) {
            const chatNotificationModel = new ChatModel(JSON.parse(response))
            if (
              notificationStore.openedChatUserId &&
              notificationStore.openedChatUserId === chatNotificationModel.senderUserId
            ) {
              chatNotificationModel.isRead = true
              this.updateReadMessage(chatNotificationModel.senderUserId as string)
            }
            notificationStore.addMessage(chatNotificationModel)
            resolve(chatNotificationModel)
          } else {
            resolve({ error: true })
          }
        })
      })
    } catch {
      return { error: true }
    }
  }
  async connectToUnavailableItems(): Promise<UnavailableItemOrderEntity | { error: true }> {
    try {
      const signalRConnection = useSignalRConnection()
      return new Promise((resolve) => {
        signalRConnection.on('OrderItemUpdateAvailability', (response: string) => {
          if (JSON.parse(response)) {
            const unavailableItemOrderEntity = JSON.parse(response) as UnavailableItemOrderEntity
            resolve(unavailableItemOrderEntity)
          } else {
            resolve({ error: true })
          }
        })
      })
    } catch {
      return { error: true }
    }
  }
}
